import React, { useState, useMemo } from 'react';

/**
 * Helper to safely extract B2C-REPORT object whether it arrives as an object
 * or a JSON string, and from any of the standard wrapper levels.
 */
export const getB2CReport = (data) => {
    if (!data) return null;
    let parsed = data;
    if (typeof data === 'string') {
        try {
            parsed = JSON.parse(data);
        } catch (e) {
            return null; // Not valid JSON (legacy HTML report)
        }
    }
    if (typeof parsed !== 'object' || parsed === null) return null;

    return (
        parsed?.data?.result?.result_json?.parsed_data?.['B2C-REPORT'] ||
        parsed?.result?.result_json?.parsed_data?.['B2C-REPORT'] ||
        parsed?.result_json?.parsed_data?.['B2C-REPORT'] ||
        parsed?.parsed_data?.['B2C-REPORT'] ||
        parsed?.['B2C-REPORT'] ||
        null
    );
};

const parseAmount = (val) => {
    if (!val) return 0;
    if (typeof val === 'number') return val;
    const cleaned = String(val).replace(/,/g, '').trim();
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
};

const formatCurrency = (val) => {
    const num = parseAmount(val);
    return '₹ ' + num.toLocaleString('en-IN');
};

const getStatusBadgeStyle = (statusStr, overdueAmt) => {
    const status = (statusStr || '').toLowerCase();
    const overdue = parseAmount(overdueAmt);

    if (overdue > 0 || status.includes('delinq') || status.includes('loss') || status.includes('suit') || status.includes('write')) {
        return {
            bg: '#fee2e2',
            text: '#991b1b',
            border: '#fca5a5',
            dot: '#dc2626',
            label: statusStr || 'Overdue'
        };
    }
    if (status.includes('closed')) {
        return {
            bg: '#ecfdf5',
            text: '#065f46',
            border: '#a7f3d0',
            dot: '#10b981',
            label: statusStr || 'Closed'
        };
    }
    return {
        bg: '#eff6ff',
        text: '#1e40af',
        border: '#bfdbfe',
        dot: '#3b82f6',
        label: statusStr || 'Active'
    };
};

const parsePaymentStatusStyle = (code) => {
    if (!code || code === '-') {
        return { bg: '#f1f5f9', color: '#64748b', border: '#cbd5e1', label: '-' };
    }
    const val = String(code).toUpperCase().trim();
    if (val === '000' || val.includes('STD') || val === '0') {
        return { bg: '#ecfdf5', color: '#047857', border: '#a7f3d0', label: 'STD' };
    }
    if (val === '030' || val === '1' || val === 'SMA') {
        return { bg: '#fefce8', color: '#854d0e', border: '#fde047', label: val };
    }
    if (val === '060' || val === '2') {
        return { bg: '#fff7ed', color: '#c2410c', border: '#fdba74', label: val };
    }
    if (val === '090' || val === '3' || val.includes('SUB') || val.includes('DBT') || val.includes('LSS')) {
        return { bg: '#fef2f2', color: '#b91c1c', border: '#fca5a5', label: val };
    }
    return { bg: '#f8fafc', color: '#334155', border: '#cbd5e1', label: val };
};

const CrifOriginalReportViewer = ({ originalCrifReport }) => {
    const [activeTab, setActiveTab] = useState('tradelines');
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [expandedTradeline, setExpandedTradeline] = useState(null);
    const [variationFilter, setVariationFilter] = useState('ALL');

    const b2cReport = useMemo(() => getB2CReport(originalCrifReport), [originalCrifReport]);

    // If not a structured JSON CRIF report, render as HTML fallback
    if (!b2cReport) {
        const htmlContent = typeof originalCrifReport === 'string' ? originalCrifReport : JSON.stringify(originalCrifReport, null, 2);
        return (
            <div
                dangerouslySetInnerHTML={{
                    __html: htmlContent
                }}
            />
        );
    }

    const header = b2cReport['HEADER-SEGMENT'] || {};
    const applicant = b2cReport['REQUEST-DATA']?.['APPLICANT-SEGMENT'] || {};
    const application = b2cReport['REQUEST-DATA']?.['APPLICATION-SEGMENT'] || {};
    const standardData = b2cReport['REPORT-DATA']?.['STANDARD-DATA'] || {};

    const variationsList = standardData['DEMOGS']?.['VARIATIONS'] || [];
    const employmentList = standardData['EMPLOYMENT-DETAILS'] || [];
    const tradelinesList = standardData['TRADELINES'] || [];

    // Flatten variations for display
    const allVariations = [];
    variationsList.forEach(group => {
        const type = group?.TYPE || 'VARIATION';
        const items = group?.VARIATION || [];
        if (Array.isArray(items)) {
            items.forEach(item => {
                allVariations.push({
                    type,
                    value: item?.VALUE || item?.['ADDRESSTEXT'] || '-',
                    reportedDt: item?.['REPORTED-DT'] || '-',
                    firstReportedDt: item?.['FIRST-REPORTED-DT'] || '-',
                    loanType: item?.['LOAN-TYPE-ASSOC'] || '-',
                    source: item?.['SOURCE-INDICATOR'] || '-'
                });
            });
        }
    });

    // Tradeline summary metrics
    const totalTradelines = tradelinesList.length;
    let activeCount = 0;
    let closedCount = 0;
    let totalDisbursed = 0;
    let totalCurrentBal = 0;
    let totalOverdue = 0;

    tradelinesList.forEach(t => {
        const status = (t['ACCOUNT-STATUS'] || '').toLowerCase();
        if (status.includes('closed')) {
            closedCount++;
        } else {
            activeCount++;
        }
        totalDisbursed += parseAmount(t['DISBURSED-AMT']);
        totalCurrentBal += parseAmount(t['CURRENT-BAL']);
        totalOverdue += parseAmount(t['OVERDUE-AMT']);
    });

    // Filter tradelines based on search and status
    const filteredTradelines = tradelinesList.filter(t => {
        const grantor = (t['CREDIT-GRANTOR'] || '').toLowerCase();
        const acctNo = (t['ACCT-NUMBER'] || '').toLowerCase();
        const acctType = (t['ACCT-TYPE'] || '').toLowerCase();
        const q = searchQuery.toLowerCase().trim();

        const matchesQuery = !q || grantor.includes(q) || acctNo.includes(q) || acctType.includes(q);

        const status = (t['ACCOUNT-STATUS'] || '').toLowerCase();
        const overdueAmt = parseAmount(t['OVERDUE-AMT']);

        let matchesStatus = true;
        if (statusFilter === 'ACTIVE') {
            matchesStatus = !status.includes('closed');
        } else if (statusFilter === 'CLOSED') {
            matchesStatus = status.includes('closed');
        } else if (statusFilter === 'OVERDUE') {
            matchesStatus = overdueAmt > 0;
        }

        return matchesQuery && matchesStatus;
    });

    // Filter variations
    const filteredVariations = allVariations.filter(v => {
        if (variationFilter === 'ALL') return true;
        return v.type.toLowerCase().includes(variationFilter.toLowerCase());
    });

    const renderApplicantIds = () => {
        const ids = applicant['IDS'] || [];
        if (!ids.length) return '-';
        return ids.map((id, i) => (
            <span key={i} style={{ marginRight: '8px' }}>
                <span style={{ backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 600, marginRight: '4px' }}>
                    {id.TYPE || 'ID'}
                </span>
                <b style={{ color: '#0f172a' }}>{id.VALUE || '-'}</b>
            </span>
        ));
    };

    const renderPhones = () => {
        const phones = applicant['PHONES'] || [];
        if (!phones.length) return '-';
        return phones.map((p, i) => (
            <span key={i} style={{ marginRight: '8px' }}>
                <b style={{ color: '#0f172a' }}>{p.VALUE || '-'}</b>
            </span>
        ));
    };

    const renderEmails = () => {
        const emails = applicant['EMAILS'] || [];
        if (!emails.length) return '-';
        return emails.map((e, i) => (
            <span key={i} style={{ marginRight: '8px', wordBreak: 'break-all' }}>
                <b style={{ color: '#0f172a' }}>{e.EMAIL || '-'}</b>
            </span>
        ));
    };

    const renderAddresses = () => {
        const addrs = applicant['ADDRESSES'] || [];
        if (!addrs.length) return '-';
        return addrs.map((a, i) => (
            <div key={i} style={{ fontSize: '12px', color: '#334155', marginBottom: i < addrs.length - 1 ? '4px' : '0' }}>
                <i className="fa fa-map-marker text-danger" style={{ marginRight: '4px' }}></i>
                {a.ADDRESSTEXT || `${a.CITY || ''} ${a.STATE || ''} ${a.PIN || ''} ${a.COUNTRY || ''}`.trim() || '-'}
            </div>
        ));
    };

    return (
        <div style={{ fontFamily: 'inherit', color: '#1e293b', width: '100%' }}>
            {/* Top Header Banner */}
            <div style={{
                background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)',
                color: '#fff',
                borderRadius: '12px',
                padding: '20px 24px',
                marginBottom: '20px',
                boxShadow: '0 4px 14px rgba(15, 23, 42, 0.15)'
            }}>
                <div className="row" style={{ alignItems: 'center' }}>
                    <div className="col-sm-7 col-xs-12">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <h3 style={{ margin: 0, fontWeight: 700, color: '#fff', fontSize: '22px' }}>
                                CRIF High Mark Original Report
                            </h3>
                            {header['STATUS'] && (
                                <span style={{
                                    backgroundColor: header['STATUS'] === 'SUCCESS' ? '#10b981' : '#f59e0b',
                                    color: '#fff',
                                    padding: '3px 10px',
                                    borderRadius: '12px',
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    {header['STATUS']}
                                </span>
                            )}
                        </div>
                        <div style={{ fontSize: '13px', color: '#93c5fd', marginTop: '6px' }}>
                            {header['PRODUCT-TYPE'] || 'CRIF Credit Report'} {header['PRODUCT-VER'] ? `(v${header['PRODUCT-VER']})` : ''}
                        </div>
                    </div>
                    <div className="col-sm-5 col-xs-12 text-right" style={{ fontSize: '12px', color: '#cbd5e1', marginTop: '8px' }}>
                        <div><b>Report ID:</b> <span style={{ fontFamily: 'monospace', color: '#fff' }}>{header['REPORT-ID'] || '-'}</span></div>
                        <div style={{ marginTop: '2px' }}><b>Issue:</b> {header['DATE-OF-ISSUE'] || '-'} &nbsp;|&nbsp; <b>Request:</b> {header['DATE-OF-REQUEST'] || '-'}</div>
                        {header['BATCH-ID'] && <div style={{ marginTop: '2px' }}><b>Batch ID:</b> <span style={{ fontFamily: 'monospace' }}>{header['BATCH-ID']}</span></div>}
                    </div>
                </div>
            </div>

            {/* Applicant Summary Card */}
            <div style={{
                background: '#fff',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                padding: '18px 22px',
                marginBottom: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
            }}>
                <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '14px', color: '#0f172a', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="fa fa-user" style={{ color: '#2563eb' }}></i>
                    Applicant Information
                </div>
                <div className="row" style={{ fontSize: '13px' }}>
                    <div className="col-md-3 col-sm-6 col-xs-12" style={{ marginBottom: '10px' }}>
                        <span style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase', display: 'block', fontWeight: 600 }}>Name</span>
                        <b style={{ fontSize: '14px', color: '#0f172a' }}>
                            {[applicant['FIRST-NAME'], applicant['MIDDLE-NAME'], applicant['LAST-NAME']].filter(Boolean).join(' ') || '-'}
                        </b>
                    </div>
                    <div className="col-md-3 col-sm-6 col-xs-12" style={{ marginBottom: '10px' }}>
                        <span style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase', display: 'block', fontWeight: 600 }}>DOB / Age / Gender</span>
                        <b style={{ color: '#0f172a' }}>
                            {applicant['DOB']?.['DOB-DT'] || '-'}
                            {applicant['DOB']?.['AGE'] ? ` (${applicant['DOB']['AGE']} yrs)` : ''}
                            {applicant['GENDER'] ? ` / ${applicant['GENDER']}` : ''}
                        </b>
                    </div>
                    <div className="col-md-3 col-sm-6 col-xs-12" style={{ marginBottom: '10px' }}>
                        <span style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase', display: 'block', fontWeight: 600 }}>Identifications (PAN / ID)</span>
                        {renderApplicantIds()}
                    </div>
                    <div className="col-md-3 col-sm-6 col-xs-12" style={{ marginBottom: '10px' }}>
                        <span style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase', display: 'block', fontWeight: 600 }}>Mobile Number</span>
                        {renderPhones()}
                    </div>
                    <div className="col-md-4 col-sm-6 col-xs-12" style={{ marginBottom: '8px' }}>
                        <span style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase', display: 'block', fontWeight: 600 }}>Email Address</span>
                        {renderEmails()}
                    </div>
                    <div className="col-md-8 col-sm-12 col-xs-12" style={{ marginBottom: '8px' }}>
                        <span style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase', display: 'block', fontWeight: 600 }}>Reported Address</span>
                        {renderAddresses()}
                    </div>
                </div>
            </div>

            {/* Metrics Ribbon */}
            <div className="row" style={{ marginBottom: '22px' }}>
                <div className="col-sm-2 col-xs-6" style={{ marginBottom: '10px' }}>
                    <div style={{
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '10px',
                        padding: '12px 14px',
                        textAlign: 'center',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                    }}>
                        <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Total Accounts</div>
                        <div style={{ fontSize: '22px', fontWeight: 700, color: '#0f172a', marginTop: '2px' }}>{totalTradelines}</div>
                    </div>
                </div>
                <div className="col-sm-2 col-xs-6" style={{ marginBottom: '10px' }}>
                    <div style={{
                        background: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        borderRadius: '10px',
                        padding: '12px 14px',
                        textAlign: 'center',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                    }}>
                        <div style={{ fontSize: '11px', color: '#166534', fontWeight: 600, textTransform: 'uppercase' }}>Active</div>
                        <div style={{ fontSize: '22px', fontWeight: 700, color: '#15803d', marginTop: '2px' }}>{activeCount}</div>
                    </div>
                </div>
                <div className="col-sm-2 col-xs-6" style={{ marginBottom: '10px' }}>
                    <div style={{
                        background: '#f1f5f9',
                        border: '1px solid #cbd5e1',
                        borderRadius: '10px',
                        padding: '12px 14px',
                        textAlign: 'center',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                    }}>
                        <div style={{ fontSize: '11px', color: '#475569', fontWeight: 600, textTransform: 'uppercase' }}>Closed</div>
                        <div style={{ fontSize: '22px', fontWeight: 700, color: '#334155', marginTop: '2px' }}>{closedCount}</div>
                    </div>
                </div>
                <div className="col-sm-2 col-xs-6" style={{ marginBottom: '10px' }}>
                    <div style={{
                        background: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        borderRadius: '10px',
                        padding: '12px 14px',
                        textAlign: 'center',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                    }}>
                        <div style={{ fontSize: '11px', color: '#1e40af', fontWeight: 600, textTransform: 'uppercase' }}>Total Disbursed</div>
                        <div style={{ fontSize: '16px', fontWeight: 700, color: '#1d4ed8', marginTop: '4px' }}>
                            {formatCurrency(totalDisbursed)}
                        </div>
                    </div>
                </div>
                <div className="col-sm-2 col-xs-6" style={{ marginBottom: '10px' }}>
                    <div style={{
                        background: '#fefce8',
                        border: '1px solid #fef08a',
                        borderRadius: '10px',
                        padding: '12px 14px',
                        textAlign: 'center',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                    }}>
                        <div style={{ fontSize: '11px', color: '#854d0e', fontWeight: 600, textTransform: 'uppercase' }}>Current Balance</div>
                        <div style={{ fontSize: '16px', fontWeight: 700, color: '#a16207', marginTop: '4px' }}>
                            {formatCurrency(totalCurrentBal)}
                        </div>
                    </div>
                </div>
                <div className="col-sm-2 col-xs-6" style={{ marginBottom: '10px' }}>
                    <div style={{
                        background: totalOverdue > 0 ? '#fef2f2' : '#f0fdf4',
                        border: totalOverdue > 0 ? '1px solid #fecaca' : '1px solid #bbf7d0',
                        borderRadius: '10px',
                        padding: '12px 14px',
                        textAlign: 'center',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                    }}>
                        <div style={{ fontSize: '11px', color: totalOverdue > 0 ? '#991b1b' : '#166534', fontWeight: 600, textTransform: 'uppercase' }}>Total Overdue</div>
                        <div style={{ fontSize: '16px', fontWeight: 700, color: totalOverdue > 0 ? '#dc2626' : '#15803d', marginTop: '4px' }}>
                            {formatCurrency(totalOverdue)}
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation Tabs (Raw JSON removed) */}
            <div style={{ display: 'flex', gap: '8px', borderBottom: '2px solid #e2e8f0', marginBottom: '20px', flexWrap: 'wrap' }}>
                <button
                    type="button"
                    onClick={() => setActiveTab('tradelines')}
                    style={{
                        padding: '11px 20px',
                        border: 'none',
                        background: 'transparent',
                        borderBottom: activeTab === 'tradelines' ? '3px solid #2563eb' : '3px solid transparent',
                        color: activeTab === 'tradelines' ? '#2563eb' : '#64748b',
                        fontWeight: activeTab === 'tradelines' ? 700 : 500,
                        cursor: 'pointer',
                        fontSize: '14px',
                        outline: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <i className="fa fa-list-alt"></i>
                    Tradelines / Accounts
                    <span style={{
                        backgroundColor: activeTab === 'tradelines' ? '#2563eb' : '#94a3b8',
                        color: '#fff',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '11px',
                        fontWeight: 700
                    }}>
                        {tradelinesList.length}
                    </span>
                </button>

                <button
                    type="button"
                    onClick={() => setActiveTab('variations')}
                    style={{
                        padding: '11px 20px',
                        border: 'none',
                        background: 'transparent',
                        borderBottom: activeTab === 'variations' ? '3px solid #2563eb' : '3px solid transparent',
                        color: activeTab === 'variations' ? '#2563eb' : '#64748b',
                        fontWeight: activeTab === 'variations' ? 700 : 500,
                        cursor: 'pointer',
                        fontSize: '14px',
                        outline: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <i className="fa fa-clone"></i>
                    Demographic Variations
                    <span style={{
                        backgroundColor: activeTab === 'variations' ? '#2563eb' : '#94a3b8',
                        color: '#fff',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '11px',
                        fontWeight: 700
                    }}>
                        {allVariations.length}
                    </span>
                </button>

                <button
                    type="button"
                    onClick={() => setActiveTab('employment')}
                    style={{
                        padding: '11px 20px',
                        border: 'none',
                        background: 'transparent',
                        borderBottom: activeTab === 'employment' ? '3px solid #2563eb' : '3px solid transparent',
                        color: activeTab === 'employment' ? '#2563eb' : '#64748b',
                        fontWeight: activeTab === 'employment' ? 700 : 500,
                        cursor: 'pointer',
                        fontSize: '14px',
                        outline: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <i className="fa fa-briefcase"></i>
                    Employment Details
                    <span style={{
                        backgroundColor: activeTab === 'employment' ? '#2563eb' : '#94a3b8',
                        color: '#fff',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '11px',
                        fontWeight: 700
                    }}>
                        {employmentList.length}
                    </span>
                </button>

                {application && (application['APPLICATION-ID'] || application['LOAN-AMT'] || application['LOAN-TYPE']) && (
                    <button
                        type="button"
                        onClick={() => setActiveTab('application')}
                        style={{
                            padding: '11px 20px',
                            border: 'none',
                            background: 'transparent',
                            borderBottom: activeTab === 'application' ? '3px solid #2563eb' : '3px solid transparent',
                            color: activeTab === 'application' ? '#2563eb' : '#64748b',
                            fontWeight: activeTab === 'application' ? 700 : 500,
                            cursor: 'pointer',
                            fontSize: '14px',
                            outline: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <i className="fa fa-file-text-o"></i>
                        Application Info
                    </button>
                )}
            </div>

            {/* TAB 1: Tradelines / Accounts */}
            {activeTab === 'tradelines' && (
                <div>
                    {/* Controls & Filter Bar */}
                    <div style={{
                        background: '#fff',
                        padding: '14px 18px',
                        borderRadius: '10px',
                        border: '1px solid #e2e8f0',
                        marginBottom: '16px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '14px',
                        flexWrap: 'wrap',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                    }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '13px', fontWeight: 600, color: '#475569', marginRight: '4px' }}>
                                Filter Accounts:
                            </span>
                            {[
                                { key: 'ALL', label: 'All', count: totalTradelines },
                                { key: 'ACTIVE', label: 'Active', count: activeCount },
                                { key: 'CLOSED', label: 'Closed', count: closedCount },
                                { key: 'OVERDUE', label: 'Overdue', count: tradelinesList.filter(x => parseAmount(x['OVERDUE-AMT']) > 0).length }
                            ].map(item => {
                                const isSelected = statusFilter === item.key;
                                return (
                                    <button
                                        key={item.key}
                                        type="button"
                                        onClick={() => setStatusFilter(item.key)}
                                        style={{
                                            border: isSelected ? '1px solid #2563eb' : '1px solid #cbd5e1',
                                            backgroundColor: isSelected ? '#eff6ff' : '#fff',
                                            color: isSelected ? '#1d4ed8' : '#475569',
                                            padding: '6px 14px',
                                            borderRadius: '20px',
                                            fontSize: '12px',
                                            fontWeight: isSelected ? 700 : 500,
                                            cursor: 'pointer',
                                            outline: 'none',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            transition: 'all 0.15s ease-in-out'
                                        }}
                                    >
                                        <span>{item.label}</span>
                                        <span style={{
                                            backgroundColor: isSelected ? '#2563eb' : '#f1f5f9',
                                            color: isSelected ? '#fff' : '#64748b',
                                            borderRadius: '10px',
                                            padding: '1px 7px',
                                            fontSize: '10px',
                                            fontWeight: 700
                                        }}>
                                            {item.count}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Search Input */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '280px' }}>
                            <div style={{ position: 'relative', width: '100%' }}>
                                <i className="fa fa-search" style={{ position: 'absolute', left: '12px', top: '10px', color: '#94a3b8', fontSize: '13px' }}></i>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search lender, account, or type..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    style={{
                                        paddingLeft: '34px',
                                        paddingRight: searchQuery ? '30px' : '12px',
                                        height: '34px',
                                        borderRadius: '8px',
                                        borderColor: '#cbd5e1',
                                        fontSize: '12px'
                                    }}
                                />
                                {searchQuery && (
                                    <i
                                        className="fa fa-times-circle"
                                        onClick={() => setSearchQuery('')}
                                        style={{ position: 'absolute', right: '10px', top: '9px', color: '#94a3b8', cursor: 'pointer', fontSize: '14px' }}
                                    ></i>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Results Counter */}
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Showing <b>{filteredTradelines.length}</b> of <b>{totalTradelines}</b> accounts</span>
                        <span style={{ fontStyle: 'italic', fontSize: '11px' }}>
                            <i className="fa fa-lightbulb-o text-warning" style={{ marginRight: '4px' }}></i>
                            Click on any row to view complete payment history & loan details
                        </span>
                    </div>

                    {/* Enhanced Tradelines Table */}
                    <div style={{
                        background: '#fff',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        overflow: 'hidden',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
                    }}>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                fontSize: '12px',
                                textAlign: 'left'
                            }}>
                                <thead>
                                    <tr style={{
                                        background: '#0f172a',
                                        color: '#f8fafc',
                                        borderBottom: '2px solid #334155',
                                        fontWeight: 600,
                                        fontSize: '11px',
                                        letterSpacing: '0.5px',
                                        textTransform: 'uppercase'
                                    }}>
                                        <th style={{ padding: '12px 14px', width: '45px', textAlign: 'center' }}>#</th>
                                        <th style={{ padding: '12px 14px', minWidth: '180px' }}>Lender (Grantor)</th>
                                        <th style={{ padding: '12px 14px', minWidth: '150px' }}>Account Details</th>
                                        <th style={{ padding: '12px 12px', textAlign: 'center', width: '90px' }}>Ownership</th>
                                        <th style={{ padding: '12px 12px', textAlign: 'center', width: '110px' }}>Status</th>
                                        <th style={{ padding: '12px 14px', textAlign: 'right', minWidth: '120px' }}>Disbursed</th>
                                        <th style={{ padding: '12px 14px', textAlign: 'right', minWidth: '110px' }}>Current Bal</th>
                                        <th style={{ padding: '12px 14px', textAlign: 'right', minWidth: '110px' }}>Overdue Amt</th>
                                        <th style={{ padding: '12px 12px', textAlign: 'center', width: '100px' }}>Security</th>
                                        <th style={{ padding: '12px 14px', minWidth: '150px' }}>Recent Track</th>
                                        <th style={{ padding: '12px 12px', textAlign: 'center', width: '70px' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredTradelines.length === 0 ? (
                                        <tr>
                                            <td colSpan="11" style={{ padding: '40px 20px', textAlign: 'center', color: '#64748b', background: '#fafbfc' }}>
                                                <i className="fa fa-search fa-2x" style={{ display: 'block', marginBottom: '8px', color: '#cbd5e1' }}></i>
                                                <b>No accounts match the selected filters.</b>
                                                <div style={{ fontSize: '12px', marginTop: '4px' }}>Try adjusting your search query or status filter.</div>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredTradelines.map((item, idx) => {
                                            const overdue = parseAmount(item['OVERDUE-AMT']);
                                            const isExpanded = expandedTradeline === idx;
                                            const statusBadge = getStatusBadgeStyle(item['ACCOUNT-STATUS'], item['OVERDUE-AMT']);

                                            // History extraction
                                            const historyItems = item['HISTORY'] || [];
                                            const paymentHist = historyItems.find(h => h.NAME === 'COMBINED-PAYMENT-HISTORY') || historyItems[0];
                                            const dates = paymentHist?.DATES ? paymentHist.DATES.split('|').filter(Boolean) : [];
                                            const values = paymentHist?.VALUES ? paymentHist.VALUES.split('|').filter(Boolean) : [];

                                            // Combined unified history
                                            const timelineMap = {};
                                            historyItems.forEach(h => {
                                                const d = (h.DATES || '').split('|').filter(Boolean);
                                                const v = (h.VALUES || '').split('|').filter(Boolean);
                                                d.forEach((dateKey, dIndex) => {
                                                    if (!timelineMap[dateKey]) timelineMap[dateKey] = { date: dateKey };
                                                    timelineMap[dateKey][h.NAME] = v[dIndex] || '-';
                                                });
                                            });
                                            const unifiedTimeline = Object.values(timelineMap);

                                            return (
                                                <React.Fragment key={idx}>
                                                    <tr
                                                        onClick={() => setExpandedTradeline(isExpanded ? null : idx)}
                                                        style={{
                                                            cursor: 'pointer',
                                                            backgroundColor: isExpanded ? '#f0f7ff' : (idx % 2 === 0 ? '#ffffff' : '#f8fafc'),
                                                            borderBottom: isExpanded ? 'none' : '1px solid #edf2f7',
                                                            borderLeft: isExpanded ? '4px solid #2563eb' : (overdue > 0 ? '4px solid #ef4444' : '4px solid transparent'),
                                                            transition: 'background-color 0.15s ease'
                                                        }}
                                                    >
                                                        {/* Index */}
                                                        <td style={{ padding: '12px 14px', textAlign: 'center', fontWeight: 600, color: '#64748b' }}>
                                                            {idx + 1}
                                                        </td>

                                                        {/* Lender */}
                                                        <td style={{ padding: '12px 14px' }}>
                                                            <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '13px' }}>
                                                                {item['CREDIT-GRANTOR'] || '-'}
                                                            </div>
                                                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '3px' }}>
                                                                {item['CREDIT-GRANTOR-TYPE'] && (
                                                                    <span style={{
                                                                        background: '#e2e8f0',
                                                                        color: '#334155',
                                                                        fontSize: '10px',
                                                                        fontWeight: 600,
                                                                        padding: '1px 6px',
                                                                        borderRadius: '4px'
                                                                    }}>
                                                                        {item['CREDIT-GRANTOR-TYPE']}
                                                                    </span>
                                                                )}
                                                                {item['CREDIT-GRANTOR-GROUP'] && (
                                                                    <span style={{ fontSize: '11px', color: '#64748b' }}>
                                                                        {item['CREDIT-GRANTOR-GROUP']}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>

                                                        {/* Account Type & Number */}
                                                        <td style={{ padding: '12px 14px' }}>
                                                            <div style={{ fontWeight: 600, color: '#1e293b' }}>
                                                                {item['ACCT-TYPE'] || '-'}
                                                            </div>
                                                            <div style={{ fontFamily: 'monospace', color: '#64748b', fontSize: '11px', marginTop: '2px' }}>
                                                                #{item['ACCT-NUMBER'] || '-'}
                                                            </div>
                                                        </td>

                                                        {/* Ownership */}
                                                        <td style={{ padding: '12px 12px', textAlign: 'center', color: '#334155' }}>
                                                            {item['OWNERSHIP-TYPE'] || '-'}
                                                        </td>

                                                        {/* Account Status Badge */}
                                                        <td style={{ padding: '12px 12px', textAlign: 'center' }}>
                                                            <span style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '5px',
                                                                padding: '4px 10px',
                                                                borderRadius: '16px',
                                                                fontSize: '11px',
                                                                fontWeight: 700,
                                                                backgroundColor: statusBadge.bg,
                                                                color: statusBadge.text,
                                                                border: `1px solid ${statusBadge.border}`
                                                            }}>
                                                                <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: statusBadge.dot }}></span>
                                                                {statusBadge.label}
                                                            </span>
                                                        </td>

                                                        {/* Disbursed Amount & Date */}
                                                        <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                                                            <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '13px' }}>
                                                                {item['DISBURSED-AMT'] ? `₹ ${item['DISBURSED-AMT']}` : '-'}
                                                            </div>
                                                            {item['DISBURSED-DT'] && (
                                                                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                                                    {item['DISBURSED-DT']}
                                                                </div>
                                                            )}
                                                        </td>

                                                        {/* Current Balance */}
                                                        <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                                                            <span style={{ fontWeight: 700, color: '#0f172a' }}>
                                                                {item['CURRENT-BAL'] ? `₹ ${item['CURRENT-BAL']}` : '₹ 0'}
                                                            </span>
                                                        </td>

                                                        {/* Overdue Amount */}
                                                        <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                                                            {overdue > 0 ? (
                                                                <span style={{
                                                                    display: 'inline-block',
                                                                    backgroundColor: '#fee2e2',
                                                                    color: '#dc2626',
                                                                    border: '1px solid #fca5a5',
                                                                    padding: '2px 8px',
                                                                    borderRadius: '6px',
                                                                    fontWeight: 700
                                                                }}>
                                                                    ₹ {item['OVERDUE-AMT']}
                                                                </span>
                                                            ) : (
                                                                <span style={{ color: '#10b981', fontWeight: 600 }}>
                                                                    ₹ 0
                                                                </span>
                                                            )}
                                                        </td>

                                                        {/* Security Status */}
                                                        <td style={{ padding: '12px 12px', textAlign: 'center' }}>
                                                            <span style={{
                                                                fontSize: '11px',
                                                                fontWeight: 600,
                                                                color: (item['SECURITY-STATUS'] || '').toLowerCase().includes('un') ? '#64748b' : '#059669',
                                                                backgroundColor: (item['SECURITY-STATUS'] || '').toLowerCase().includes('un') ? '#f1f5f9' : '#ecfdf5',
                                                                padding: '2px 8px',
                                                                borderRadius: '4px'
                                                            }}>
                                                                {item['SECURITY-STATUS'] || '-'}
                                                            </span>
                                                        </td>

                                                        {/* Recent Track Quick Peek (up to 4 latest months) */}
                                                        <td style={{ padding: '12px 14px' }}>
                                                            {dates.length === 0 ? (
                                                                <span style={{ color: '#94a3b8', fontSize: '11px' }}>-</span>
                                                            ) : (
                                                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                                    {dates.slice(0, 4).map((d, dIdx) => {
                                                                        const val = values[dIdx] || '-';
                                                                        const pStyle = parsePaymentStatusStyle(val);
                                                                        return (
                                                                            <span
                                                                                key={dIdx}
                                                                                title={`${d.replace(':', ' ')}: ${val}`}
                                                                                style={{
                                                                                    backgroundColor: pStyle.bg,
                                                                                    color: pStyle.color,
                                                                                    border: `1px solid ${pStyle.border}`,
                                                                                    padding: '2px 5px',
                                                                                    borderRadius: '4px',
                                                                                    fontSize: '10px',
                                                                                    fontWeight: 700
                                                                                }}
                                                                            >
                                                                                {pStyle.label}
                                                                            </span>
                                                                        );
                                                                    })}
                                                                    {dates.length > 4 && (
                                                                        <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 600 }}>
                                                                            +{dates.length - 4}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </td>

                                                        {/* Action / Expand */}
                                                        <td style={{ padding: '12px 12px', textAlign: 'center' }}>
                                                            <button
                                                                type="button"
                                                                className="btn btn-xs"
                                                                style={{
                                                                    // backgroundColor: isExpanded ? '#2563eb' : '#f1f5f9',
                                                                    color: isExpanded ? '#475569' : '#475569',
                                                                    border: 'none',
                                                                    // borderRadius: '100%',
                                                                    // width: '26px',
                                                                    // height: '26px',
                                                                    lineHeight: '24px',
                                                                    padding: 0
                                                                }}
                                                            >
                                                                <i className={`fa ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                                                            </button>
                                                        </td>
                                                    </tr>

                                                    {/* Expanded Tradeline Details & Repayment Ledger */}
                                                    {isExpanded && (
                                                        <tr>
                                                            <td colSpan="11" style={{
                                                                padding: '0 20px 20px 20px',
                                                                backgroundColor: '#f0f7ff',
                                                                borderBottom: '2px solid #cbd5e1',
                                                                borderLeft: '4px solid #2563eb'
                                                            }}>
                                                                <div style={{
                                                                    background: '#fff',
                                                                    borderRadius: '10px',
                                                                    border: '1px solid #bfdbfe',
                                                                    padding: '20px',
                                                                    boxShadow: '0 4px 10px rgba(37, 99, 235, 0.08)'
                                                                }}>
                                                                    {/* Expanded Header */}
                                                                    <div style={{
                                                                        display: 'flex',
                                                                        justifyContent: 'space-between',
                                                                        alignItems: 'center',
                                                                        borderBottom: '1px solid #e2e8f0',
                                                                        paddingBottom: '12px',
                                                                        marginBottom: '16px'
                                                                    }}>
                                                                        <div>
                                                                            <h4 style={{ margin: 0, fontWeight: 700, color: '#0f172a', fontSize: '16px' }}>
                                                                                {item['CREDIT-GRANTOR']} &mdash; {item['ACCT-TYPE']}
                                                                            </h4>
                                                                            <span style={{ fontSize: '12px', color: '#64748b' }}>
                                                                                Account Number: <b style={{ fontFamily: 'monospace', color: '#1e293b' }}>{item['ACCT-NUMBER']}</b>
                                                                            </span>
                                                                        </div>
                                                                        <span style={{
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            gap: '6px',
                                                                            padding: '5px 12px',
                                                                            borderRadius: '20px',
                                                                            fontSize: '12px',
                                                                            fontWeight: 700,
                                                                            backgroundColor: statusBadge.bg,
                                                                            color: statusBadge.text,
                                                                            border: `1px solid ${statusBadge.border}`
                                                                        }}>
                                                                            <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: statusBadge.dot }}></span>
                                                                            {statusBadge.label}
                                                                        </span>
                                                                    </div>

                                                                    {/* Loan Snapshot 4x2 Grid */}
                                                                    <div className="row" style={{ fontSize: '12px', marginBottom: '18px' }}>
                                                                        <div className="col-md-3 col-sm-6 col-xs-6" style={{ marginBottom: '12px' }}>
                                                                            <span style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase', display: 'block', fontWeight: 600 }}>Installment Amount</span>
                                                                            <b style={{ fontSize: '13px', color: '#0f172a' }}>{item['INSTALLMENT-AMT'] ? `₹ ${item['INSTALLMENT-AMT']}` : '-'}</b>
                                                                        </div>
                                                                        <div className="col-md-3 col-sm-6 col-xs-6" style={{ marginBottom: '12px' }}>
                                                                            <span style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase', display: 'block', fontWeight: 600 }}>Interest Rate</span>
                                                                            <b style={{ fontSize: '13px', color: '#0f172a' }}>{item['INTEREST-RATE'] ? `${item['INTEREST-RATE']}%` : '-'}</b>
                                                                        </div>
                                                                        <div className="col-md-3 col-sm-6 col-xs-6" style={{ marginBottom: '12px' }}>
                                                                            <span style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase', display: 'block', fontWeight: 600 }}>Repayment Tenure</span>
                                                                            <b style={{ fontSize: '13px', color: '#0f172a' }}>{item['REPAYMENT-TENURE'] || item['ORIGINAL-TERM'] || '-'}</b>
                                                                        </div>
                                                                        <div className="col-md-3 col-sm-6 col-xs-6" style={{ marginBottom: '12px' }}>
                                                                            <span style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase', display: 'block', fontWeight: 600 }}>Last Payment Date</span>
                                                                            <b style={{ fontSize: '13px', color: '#0f172a' }}>{item['LAST-PAYMENT-DT'] || '-'}</b>
                                                                        </div>
                                                                        <div className="col-md-3 col-sm-6 col-xs-6" style={{ marginBottom: '8px' }}>
                                                                            <span style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase', display: 'block', fontWeight: 600 }}>Closed Date</span>
                                                                            <b style={{ fontSize: '13px', color: '#0f172a' }}>{item['CLOSED-DT'] || '-'}</b>
                                                                        </div>
                                                                        <div className="col-md-3 col-sm-6 col-xs-6" style={{ marginBottom: '8px' }}>
                                                                            <span style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase', display: 'block', fontWeight: 600 }}>Reported Date</span>
                                                                            <b style={{ fontSize: '13px', color: '#0f172a' }}>{item['REPORTED-DT'] || '-'}</b>
                                                                        </div>
                                                                        <div className="col-md-3 col-sm-6 col-xs-6" style={{ marginBottom: '8px' }}>
                                                                            <span style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase', display: 'block', fontWeight: 600 }}>Written Off Amount</span>
                                                                            <b style={{ fontSize: '13px', color: parseAmount(item['WRITE-OFF-AMT']) > 0 ? '#dc2626' : '#0f172a' }}>
                                                                                {item['WRITE-OFF-AMT'] ? `₹ ${item['WRITE-OFF-AMT']}` : '₹ 0'}
                                                                            </b>
                                                                        </div>
                                                                        <div className="col-md-3 col-sm-6 col-xs-6" style={{ marginBottom: '8px' }}>
                                                                            <span style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase', display: 'block', fontWeight: 600 }}>Settlement Amount</span>
                                                                            <b style={{ fontSize: '13px', color: '#0f172a' }}>{item['SETTLEMENT-AMT'] ? `₹ ${item['SETTLEMENT-AMT']}` : '-'}</b>
                                                                        </div>
                                                                    </div>

                                                                    {/* Month-by-Month Visual Chips */}
                                                                    <div style={{ marginBottom: '18px' }}>
                                                                        <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '10px', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                            <i className="fa fa-calendar" style={{ color: '#2563eb' }}></i>
                                                                            Monthly Payment Track
                                                                        </div>
                                                                        {dates.length === 0 ? (
                                                                            <div style={{ color: '#94a3b8', fontSize: '12px', fontStyle: 'italic' }}>
                                                                                No month-by-month payment track records available for this account.
                                                                            </div>
                                                                        ) : (
                                                                            <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px' }}>
                                                                                {dates.map((dt, dIdx) => {
                                                                                    const val = values[dIdx] || '-';
                                                                                    const pStyle = parsePaymentStatusStyle(val);
                                                                                    return (
                                                                                        <div
                                                                                            key={dIdx}
                                                                                            style={{
                                                                                                minWidth: '82px',
                                                                                                border: `1px solid ${pStyle.border}`,
                                                                                                background: pStyle.bg,
                                                                                                borderRadius: '8px',
                                                                                                padding: '8px 6px',
                                                                                                textAlign: 'center'
                                                                                            }}
                                                                                        >
                                                                                            <div style={{ color: '#475569', fontWeight: 600, fontSize: '10px', textTransform: 'uppercase' }}>
                                                                                                {dt.replace(':', ' ')}
                                                                                            </div>
                                                                                            <div style={{
                                                                                                fontWeight: 700,
                                                                                                color: pStyle.color,
                                                                                                marginTop: '3px',
                                                                                                fontSize: '11px'
                                                                                            }}>
                                                                                                {val}
                                                                                            </div>
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    {/* Historical Ledger Table (if multiple history metrics exist) */}
                                                                    {unifiedTimeline.length > 0 && (
                                                                        <div>
                                                                            <div style={{ fontWeight: 700, fontSize: '12px', marginBottom: '8px', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                                                                Detailed Monthly History Ledger
                                                                            </div>
                                                                            <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                                                                                <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse', textAlign: 'left' }}>
                                                                                    <thead style={{ background: '#f8fafc', color: '#475569', borderBottom: '1px solid #e2e8f0' }}>
                                                                                        <tr>
                                                                                            <th style={{ padding: '8px 12px' }}>Month / Year</th>
                                                                                            <th style={{ padding: '8px 12px' }}>Payment Status</th>
                                                                                            <th style={{ padding: '8px 12px', textAlign: 'right' }}>High Credit</th>
                                                                                            <th style={{ padding: '8px 12px', textAlign: 'right' }}>Current Balance</th>
                                                                                            <th style={{ padding: '8px 12px', textAlign: 'right' }}>Amount Paid</th>
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody>
                                                                                        {unifiedTimeline.map((row, rIdx) => {
                                                                                            const payVal = row['COMBINED-PAYMENT-HISTORY'] || '-';
                                                                                            const pStyle = parsePaymentStatusStyle(payVal);
                                                                                            return (
                                                                                                <tr key={rIdx} style={{ borderBottom: '1px solid #f1f5f9', background: rIdx % 2 === 0 ? '#fff' : '#fcfcfd' }}>
                                                                                                    <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1e293b' }}>
                                                                                                        {row.date ? row.date.replace(':', ' ') : '-'}
                                                                                                    </td>
                                                                                                    <td style={{ padding: '8px 12px' }}>
                                                                                                        <span style={{
                                                                                                            backgroundColor: pStyle.bg,
                                                                                                            color: pStyle.color,
                                                                                                            border: `1px solid ${pStyle.border}`,
                                                                                                            padding: '2px 6px',
                                                                                                            borderRadius: '4px',
                                                                                                            fontWeight: 700,
                                                                                                            fontSize: '10px'
                                                                                                        }}>
                                                                                                            {payVal}
                                                                                                        </span>
                                                                                                    </td>
                                                                                                    <td style={{ padding: '8px 12px', textAlign: 'right', color: '#334155' }}>
                                                                                                        {row['HIGH-CREDIT-HISTORY'] ? `₹ ${row['HIGH-CREDIT-HISTORY']}` : '-'}
                                                                                                    </td>
                                                                                                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>
                                                                                                        {row['CURRENT-BALANCE-HISTORY'] ? `₹ ${row['CURRENT-BALANCE-HISTORY']}` : '-'}
                                                                                                    </td>
                                                                                                    <td style={{ padding: '8px 12px', textAlign: 'right', color: '#64748b' }}>
                                                                                                        {row['AMT-PAID-HISTORY'] && row['AMT-PAID-HISTORY'] !== '-' ? `₹ ${row['AMT-PAID-HISTORY']}` : '-'}
                                                                                                    </td>
                                                                                                </tr>
                                                                                            );
                                                                                        })}
                                                                                    </tbody>
                                                                                </table>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 2: Demographic Variations */}
            {activeTab === 'variations' && (
                <div>
                    {/* Variations Filter */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>Filter Type:</span>
                        {['ALL', 'NAME', 'PHONE', 'EMAIL', 'ADDRESS', 'PAN', 'DOB'].map(ft => (
                            <button
                                key={ft}
                                type="button"
                                onClick={() => setVariationFilter(ft)}
                                style={{
                                    borderRadius: '20px',
                                    padding: '4px 14px',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    backgroundColor: variationFilter === ft ? '#2563eb' : '#f1f5f9',
                                    color: variationFilter === ft ? '#fff' : '#475569',
                                    border: '1px solid ' + (variationFilter === ft ? '#2563eb' : '#cbd5e1'),
                                    cursor: 'pointer',
                                    outline: 'none'
                                }}
                            >
                                {ft}
                            </button>
                        ))}
                    </div>

                    <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
                                <thead>
                                    <tr style={{ background: '#0f172a', color: '#f8fafc', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        <th style={{ padding: '12px 14px', width: '45px', textAlign: 'center' }}>#</th>
                                        <th style={{ padding: '12px 14px' }}>Variation Type</th>
                                        <th style={{ padding: '12px 14px' }}>Reported Value</th>
                                        <th style={{ padding: '12px 14px' }}>Reported Date</th>
                                        <th style={{ padding: '12px 14px' }}>First Reported Date</th>
                                        <th style={{ padding: '12px 14px' }}>Associated Loan Type</th>
                                        <th style={{ padding: '12px 14px' }}>Source</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredVariations.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>
                                                No demographic variations found for this filter.
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredVariations.map((v, i) => (
                                            <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                                <td style={{ padding: '12px 14px', textAlign: 'center', fontWeight: 600, color: '#64748b' }}>{i + 1}</td>
                                                <td style={{ padding: '12px 14px' }}>
                                                    <span style={{ backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                                                        {v.type}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '12px 14px', fontWeight: 600, color: '#0f172a' }}>{v.value}</td>
                                                <td style={{ padding: '12px 14px' }}>{v.reportedDt}</td>
                                                <td style={{ padding: '12px 14px' }}>{v.firstReportedDt}</td>
                                                <td style={{ padding: '12px 14px' }}>{v.loanType}</td>
                                                <td style={{ padding: '12px 14px' }}>{v.source}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 3: Employment Details */}
            {activeTab === 'employment' && (
                <div>
                    <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
                                <thead>
                                    <tr style={{ background: '#0f172a', color: '#f8fafc', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        <th style={{ padding: '12px 14px', width: '45px', textAlign: 'center' }}>#</th>
                                        <th style={{ padding: '12px 14px' }}>Occupation</th>
                                        <th style={{ padding: '12px 14px' }}>Account Type</th>
                                        <th style={{ padding: '12px 14px' }}>First Reported Date</th>
                                        <th style={{ padding: '12px 14px' }}>Last Reported Date</th>
                                        <th style={{ padding: '12px 14px' }}>Source Indicator</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {employmentList.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>
                                                No employment details reported.
                                            </td>
                                        </tr>
                                    ) : (
                                        employmentList.map((empWrap, i) => {
                                            const emp = empWrap['EMPLOYMENT-DETAIL'] || empWrap;
                                            return (
                                                <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                                    <td style={{ padding: '12px 14px', textAlign: 'center', fontWeight: 600, color: '#64748b' }}>{i + 1}</td>
                                                    <td style={{ padding: '12px 14px' }}><b style={{ color: '#0f172a' }}>{emp['OCCUPATION'] || '-'}</b></td>
                                                    <td style={{ padding: '12px 14px' }}>{emp['ACCT-TYPE'] || '-'}</td>
                                                    <td style={{ padding: '12px 14px' }}>{emp['FIRST-REPORTED-DT'] || '-'}</td>
                                                    <td style={{ padding: '12px 14px' }}>{emp['LAST-REPORTED-DT'] || '-'}</td>
                                                    <td style={{ padding: '12px 14px' }}>{emp['SOURCE-INDICATOR'] || '-'}</td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 4: Application Segment (if present) */}
            {activeTab === 'application' && (
                <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '22px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                    <h5 style={{ fontWeight: 700, margin: '0 0 16px 0', color: '#0f172a', fontSize: '15px' }}>
                        <i className="fa fa-file-text-o text-primary" style={{ marginRight: '8px' }}></i>
                        Inquiry / Application Details
                    </h5>
                    <div className="row" style={{ fontSize: '13px' }}>
                        <div className="col-sm-4 col-xs-6" style={{ marginBottom: '12px' }}>
                            <span style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase', display: 'block', fontWeight: 600 }}>Application ID</span>
                            <b style={{ color: '#0f172a' }}>{application['APPLICATION-ID'] || '-'}</b>
                        </div>
                        <div className="col-sm-4 col-xs-6" style={{ marginBottom: '12px' }}>
                            <span style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase', display: 'block', fontWeight: 600 }}>Loan Amount</span>
                            <b style={{ color: '#1d4ed8' }}>{application['LOAN-AMT'] ? `₹ ${application['LOAN-AMT']}` : '-'}</b>
                        </div>
                        <div className="col-sm-4 col-xs-6" style={{ marginBottom: '12px' }}>
                            <span style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase', display: 'block', fontWeight: 600 }}>Loan Type</span>
                            <b style={{ color: '#0f172a' }}>{application['LOAN-TYPE'] || '-'}</b>
                        </div>
                        <div className="col-sm-4 col-xs-6" style={{ marginBottom: '12px' }}>
                            <span style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase', display: 'block', fontWeight: 600 }}>Credit Inquiry Purpose</span>
                            <b style={{ color: '#0f172a' }}>{application['CREDIT-INQ-PURPS-TYPE'] || '-'}</b>
                        </div>
                        <div className="col-sm-4 col-xs-6" style={{ marginBottom: '12px' }}>
                            <span style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase', display: 'block', fontWeight: 600 }}>Inquiry Stage</span>
                            <b style={{ color: '#0f172a' }}>{application['CREDIT-INQUIRY-STAGE'] || '-'}</b>
                        </div>
                        <div className="col-sm-4 col-xs-6" style={{ marginBottom: '12px' }}>
                            <span style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase', display: 'block', fontWeight: 600 }}>Inquiry Unique Ref</span>
                            <b style={{ color: '#0f172a' }}>{application['INQUIRY-UNIQUE-REF-NO'] || '-'}</b>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CrifOriginalReportViewer;
